import { getPaymentTimelineOptions, listAllPaymentsOptions } from "@payezy/api-client/admin/query";
import {
  Badge, Button, Card, CardBody, CardHeader, DescriptionList, EmptyState, formatDate, formatUSD, humanize, PageHeader, Select, shortId, Table, TD, TH, THead, TR,
} from "@payezy/ui";
import { keepPreviousData, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { paymentTone } from "../lib/staff";

const statuses = ["pending_approval", "approved", "processing", "completed", "failed", "returned", "canceled", "refunded"];
const PAGE = 50;

export function PaymentsPage() {
  const search = useSearch({ from: "/app/payments" });
  const navigate = useNavigate({ from: "/payments" });
  const page = search.page ?? 0;
  const list = useQuery({
    ...listAllPaymentsOptions({ query: { org_id: search.org_id, status: search.status, limit: PAGE, offset: page * PAGE } }),
    placeholderData: keepPreviousData,
  });
  const items = list.data?.items ?? [];
  return (
    <>
      <PageHeader title="Payments" description="All money movement across customers, newest first." />
      <div className="mb-4 flex items-center gap-3">
        <Select className="w-56" value={search.status ?? ""} onChange={(e) => navigate({ search: (s) => ({ ...s, status: e.target.value || undefined, page: undefined }) })}>
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
        </Select>
        {search.org_id && (
          <Button variant="secondary" size="sm" onClick={() => navigate({ search: (s) => ({ ...s, org_id: undefined }) })}>
            Organization {shortId(search.org_id)} ✕
          </Button>
        )}
      </div>
      <Card>
        <Table>
          <THead><TR><TH>Created</TH><TH>Organization</TH><TH>Kind</TH><TH>Recipient</TH><TH>Status</TH><TH className="text-right">Amount</TH></TR></THead>
          <tbody>
            {items.map((p) => (
              <TR key={p.id} className="hover:bg-ink-50">
                <TD><Link to="/payments/$paymentId" params={{ paymentId: p.id }} className="hover:underline">{formatDate(p.created_at, true)}</Link></TD>
                <TD><Link to="/customers/$orgId" params={{ orgId: p.org_id }} className="font-mono text-xs hover:underline">{shortId(p.org_id)}</Link></TD>
                <TD>{humanize(p.kind)} · {p.rail}</TD>
                <TD>{p.recipient ?? "—"}</TD>
                <TD><Badge tone={paymentTone(p.status)}>{humanize(p.status)}</Badge></TD>
                <TD className="text-right tabular-nums">{p.direction === "outbound" ? "−" : "+"}{formatUSD(p.amount)}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
        {!list.isLoading && items.length === 0 && <EmptyState title="No payments" />}
        <div className="flex justify-end gap-2 border-t border-ink-100 px-5 py-3">
          <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => navigate({ search: (s) => ({ ...s, page: page - 1 }) })}>Previous</Button>
          <Button size="sm" variant="secondary" disabled={items.length < PAGE} onClick={() => navigate({ search: (s) => ({ ...s, page: page + 1 }) })}>Next</Button>
        </div>
      </Card>
    </>
  );
}

export function PaymentPage() {
  const { paymentId } = useParams({ from: "/app/payments/$paymentId" });
  const t = useSuspenseQuery({
    ...getPaymentTimelineOptions({ path: { paymentId } }),
    refetchInterval: (q) => (["completed", "failed", "canceled", "returned", "refunded", "rejected"].includes(q.state.data?.payment.status ?? "") ? false : 5_000),
  }).data;
  const p = t.payment;
  return (
    <>
      <div className="mb-1 text-sm text-ink-500"><Link to="/payments" className="hover:underline">Payments</Link> / {shortId(p.id)}</div>
      <PageHeader
        title={<span className="flex items-center gap-3">{p.direction === "outbound" ? "−" : "+"}{formatUSD(p.amount)}<Badge tone={paymentTone(p.status)}>{humanize(p.status)}</Badge></span>}
        description={`${humanize(p.kind)} via ${p.rail}${p.recipient ? ` to ${p.recipient}` : ""}`}
        actions={<Link to="/customers/$orgId" params={{ orgId: p.org_id }}><Button variant="secondary">Open customer</Button></Link>}
      />
      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader title="Details" />
          <CardBody>
            <DescriptionList
              items={[
                { label: "Payment", value: p.id, mono: true },
                { label: "Account", value: p.account_id, mono: true },
                { label: "Fee", value: formatUSD(p.fee) },
                { label: "Destination", value: p.dest_asset ? `${p.dest_asset} on ${p.dest_network}` : "—" },
                { label: "Tx hash", value: p.tx_hash ?? "—", mono: true },
                { label: "Memo", value: p.memo || "—" },
                { label: "Failure", value: p.failure_reason ? `${p.failure_code ?? ""} ${p.failure_reason}` : "—" },
                { label: "Created by", value: p.created_by_staff_id ? `staff ${shortId(p.created_by_staff_id)}` : p.created_by_user_id ? shortId(p.created_by_user_id) : "system", mono: true },
                { label: "Workflow", value: p.workflow_id ?? "—", mono: true },
                { label: "Trace", value: p.trace_id ?? "—", mono: true },
              ]}
            />
          </CardBody>
        </Card>
        <div className="flex flex-col gap-6 lg:col-span-2">
          <Card>
            <CardHeader title="Status history" />
            <CardBody>
              <ol className="relative border-l border-ink-200 pl-5">
                {t.events.map((e, i) => (
                  <li key={i} className="mb-4 last:mb-0">
                    <span className="absolute -left-1.5 mt-1.5 size-3 rounded-full bg-ink-300 ring-4 ring-white" />
                    <p className="text-sm font-medium">{humanize(e.status)}{e.from_status ? <span className="font-normal text-ink-500"> from {humanize(e.from_status)}</span> : null}</p>
                    <p className="text-xs text-ink-500">{formatDate(e.at, true)}{e.message ? ` · ${e.message}` : ""}</p>
                  </li>
                ))}
              </ol>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Partner legs" description="Each movement Payezy instructed at a partner." />
            <Table>
              <THead><TR><TH>#</TH><TH>Partner</TH><TH>Operation</TH><TH>Status</TH><TH>Reference</TH><TH className="text-right">Amount</TH></TR></THead>
              <tbody>
                {t.legs.map((l) => (
                  <TR key={l.id}>
                    <TD>{l.seq}</TD>
                    <TD>{l.partner}</TD>
                    <TD>{humanize(l.operation)}</TD>
                    <TD><Badge tone={paymentTone(l.status)}>{humanize(l.status)}</Badge>{l.failure && <p className="mt-1 text-xs text-red-600">{l.failure}</p>}</TD>
                    <TD className="font-mono text-xs">{l.partner_ref ?? "—"}</TD>
                    <TD className="text-right tabular-nums">{formatUSD(l.amount)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
            {t.legs.length === 0 && <EmptyState title="No partner legs" />}
          </Card>
          {t.approvals.length > 0 && (
            <Card>
              <CardHeader title="Customer approvals" />
              <Table>
                <THead><TR><TH>When</TH><TH>Approver</TH><TH>Decision</TH><TH>Comment</TH></TR></THead>
                <tbody>
                  {t.approvals.map((a, i) => (
                    <TR key={i}><TD>{formatDate(a.at, true)}</TD><TD>{a.email ?? shortId(a.user_id)}</TD><TD>{a.decision}</TD><TD>{a.comment || "—"}</TD></TR>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
          <Card>
            <CardHeader title="Audit trail" />
            <Table>
              <THead><TR><TH>When</TH><TH>Actor</TH><TH>Action</TH><TH>Reason</TH></TR></THead>
              <tbody>
                {t.audit.map((e) => (
                  <TR key={e.id}><TD>{formatDate(e.occurred_at, true)}</TD><TD>{e.actor_type} {shortId(e.actor_id)}</TD><TD className="font-mono text-xs">{e.action}</TD><TD>{e.reason || "—"}</TD></TR>
                ))}
              </tbody>
            </Table>
            {t.audit.length === 0 && <EmptyState title="No audit events" />}
          </Card>
        </div>
      </div>
    </>
  );
}
